// IM-JRN-016 (IM-UX-150) — Gráfico principal "Previsto × realizado ao longo dos meses". Barras = realizado, linha com
// marcadores = previsto (um eixo só, em reais). Paleta validada (realizado #00918C, previsto #B07610, contraste ≥ 3:1,
// separação para daltonismo). Identidade nunca só pela cor: legenda com forma, dica por mês (passar o mouse ou focar) e
// a tabela com os mesmos valores, sempre disponível.
import { useState } from 'react';
import { fmtBRL, fmtPct, MONTH_LABEL, MONTH_NAME, TRACK_STATUS_LABEL, type TrackMonth } from '@intelimove/contracts';

const W = 720, H = 240, L = 64, R = 12, T = 14, Bm = 28;
const short = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : v >= 1_000 ? `${Math.round(v / 1_000).toLocaleString('pt-BR')} mil` : String(Math.round(v)));

export function PlanChart({ months, title = 'Previsto × realizado por mês' }: { months: readonly TrackMonth[]; title?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...months.map((m) => Math.max(m.target, m.actual ?? 0)));
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const y = (v: number) => T + (H - T - Bm) * (1 - v / top);
  const band = (W - L - R) / 12;
  const x = (i: number) => L + band * i + band / 2;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => top * f);
  const line = months.map((m, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(m.target).toFixed(1)}`).join(' ');
  const h = hover !== null ? months[hover] : null;
  return (
    <figure className="pl-chart">
      <figcaption>
        <b>{title}</b>
        <span className="pl-legend" aria-hidden="true"><span><i className="pl-sw-bar" />Realizado</span><span><i className="pl-sw-line" />Previsto</span></span>
      </figcaption>
      <div className="pl-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}. Valores na tabela abaixo.`} onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => <g key={t}><line x1={L} x2={W - R} y1={y(t)} y2={y(t)} className="pl-gridline" /><text x={L - 8} y={y(t) + 4} className="pl-axis" textAnchor="end">{short(t)}</text></g>)}
          {months.map((m, i) => (
            <g key={m.month}>
              {m.actual !== null && <path className="pl-bar" d={bar(x(i) - band * 0.3, y(m.actual), band * 0.6, y(0) - y(m.actual))} />}
              <text x={x(i)} y={H - 8} textAnchor="middle" className="pl-axis">{MONTH_LABEL[m.month - 1]}</text>
              <rect x={x(i) - band / 2} y={T} width={band} height={H - T - Bm} fill="transparent" tabIndex={0} aria-label={`${MONTH_NAME[m.month - 1]}: previsto ${fmtBRL(m.target)}, realizado ${m.actual === null ? 'sem registro' : fmtBRL(m.actual)}`}
                onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} />
            </g>
          ))}
          <path d={line} className="pl-line" />
          {months.map((m, i) => <circle key={m.month} cx={x(i)} cy={y(m.target)} r={4.5} className="pl-dot" />)}
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - Bm} className="pl-cross" />}
        </svg>
        {h && (
          <div className="pl-tip" style={{ left: `${(x(hover!) / W) * 100}%` }} role="status">
            <b>{MONTH_NAME[h.month - 1]}</b>
            <span>Previsto {fmtBRL(h.target)}</span>
            <span>Realizado {h.actual === null ? '—' : fmtBRL(h.actual)}</span>
            {h.deviationPct !== null && <span>Desvio {fmtPct(h.deviationPct, true)}</span>}
          </div>
        )}
      </div>
      <details className="xp-why pl-table-toggle">
        <summary>Ver os valores em tabela</summary>
        <div className="pl-scroll">
          <table className="pl-table">
            <thead><tr><th scope="col">Mês</th><th scope="col">Previsto</th><th scope="col">Realizado</th><th scope="col">Desvio</th><th scope="col">Acumulado previsto</th><th scope="col">Acumulado realizado</th><th scope="col">Situação</th></tr></thead>
            <tbody>{months.map((m) => <tr key={m.month}><th scope="row">{MONTH_NAME[m.month - 1]}</th><td>{fmtBRL(m.target)}</td><td>{fmtBRL(m.actual)}</td><td>{m.deviationPct === null ? '—' : `${fmtBRL(m.deviation)} (${fmtPct(m.deviationPct, true)})`}</td><td>{fmtBRL(m.cumTarget)}</td><td>{fmtBRL(m.cumActual)}</td><td>{TRACK_STATUS_LABEL[m.status]}</td></tr>)}</tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/** Barra com as pontas de cima arredondadas (4px) e a base reta no zero. */
function bar(x: number, y: number, w: number, h: number) {
  const r = Math.min(4, w / 2, h);
  if (h <= 0) return '';
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

/** Barras mensais simples de uma série (metas), com valores em tabela. */
export function MonthBars({ values, label }: { values: readonly number[]; label: string }) {
  const max = Math.max(1, ...values);
  return (
    <div className="pl-mini" role="img" aria-label={`${label}: ${values.map((v, i) => `${MONTH_LABEL[i]} ${fmtBRL(v)}`).join(', ')}`}>
      {values.map((v, i) => <span key={i} title={`${MONTH_NAME[i]}: ${fmtBRL(v)}`}><i style={{ height: `${Math.max(2, (v / max) * 100)}%` }} /><small>{MONTH_LABEL[i]}</small></span>)}
    </div>
  );
}
